import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.min.css';
import Select from 'react-select';
import { ReactComponent as FilterIcon } from '../../../images/v2/responseList/filter.svg';
import { ReactComponent as FilterIconActive } from '../../../images/v2/responseList/filter_active.svg';
import { convertTimeStampToUnix } from '../../common/Functions';
import AssessmentFilterInline from './AssessmentFilterInline';
import TagsFilterInline from './TagsFilterInline';
import { Filter, SortableParam, TableHeaderProps } from './types';

const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  sorting,
  filters,
  onFilterChange,
  selectedFilters,
  onSortChange,
  selectedSort,
  columnWidths,
  columnMinWidths,
  startColumnResize,
}) => {
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [dynamicOptions, setDynamicOptions] = useState<
    Record<string, Array<{ value: any; label: string }>>
  >({});
  const filterRef = useRef<HTMLDivElement>(null);
  const { getIdTokenClaims } = useAuth0();

  // Helper function to get filter key as string
  const getFilterKey = (filter: Filter): string => {
    const queryParam = filter.query_param || filter.queryParam;
    if (typeof queryParam === 'string') {
      return queryParam;
    }
    if (typeof queryParam === 'object' && queryParam.param1) {
      return queryParam.param1;
    }
    return '';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setOpenFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch dynamic options for API-based filters
  useEffect(() => {
    const fetchDynamicOptions = async () => {
      if (!filters) return;

      for (const filter of filters) {
        if (
          filter.fetch_options_api &&
          typeof filter.fetch_options_api === 'string' &&
          filter.fetch_options_api.startsWith('http')
        ) {
          try {
            const getidToken = await getIdTokenClaims();
            const idToken = getidToken.__raw;

            const response = await fetch(filter.fetch_options_api, {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            });

            if (response.ok) {
              const data = await response.json();
              console.log(
                'Filter API Response for',
                filter.query_param || filter.queryParam,
                ':',
                data
              );

              // Handle different response formats
              let optionsArray = [];
              if (Array.isArray(data)) {
                optionsArray = data;
              } else if (data.results && Array.isArray(data.results)) {
                optionsArray = data.results;
              } else if (data.data && Array.isArray(data.data)) {
                optionsArray = data.data;
              } else if (data.users && Array.isArray(data.users)) {
                optionsArray = data.users;
              } else {
                console.warn(
                  'Unexpected API response format for filter options:',
                  data
                );
                optionsArray = [];
              }

              const filterKey = getFilterKey(filter);
              setDynamicOptions(prev => ({
                ...prev,
                [filterKey]: optionsArray.map(item => {
                  // Check if this is a user filter by looking at the API endpoint
                  const isUserFilter =
                    typeof filter.fetch_options_api === 'string' &&
                    filter.fetch_options_api.includes('/users');

                  const itemObj =
                    typeof item === 'string'
                      ? { value: item, label: item }
                      : item;
                  return {
                    value: isUserFilter
                      ? itemObj.email || itemObj.value
                      : itemObj.id || itemObj.value || itemObj,
                    label: isUserFilter
                      ? itemObj.email || itemObj.label
                      : itemObj.name ||
                        itemObj.label ||
                        itemObj.title ||
                        itemObj.email ||
                        itemObj.value ||
                        itemObj,
                  };
                }),
              }));
            }
          } catch (error) {
            console.error(
              `Error fetching options for ${filter.query_param || filter.queryParam}:`,
              error
            );
          }
        }
      }
    };

    fetchDynamicOptions();
  }, [filters, getIdTokenClaims]);

  const getFilterForColumn = (columnName: string): Filter | undefined => {
    return filters?.find(
      filter =>
        filter.placement?.location === 'table_column' &&
        filter.placement?.column === columnName
    );
  };

  const getSortableParamForColumn = (
    columnKey: string
  ): SortableParam | undefined => {
    return sorting?.sortable_params?.find(param => param.param === columnKey);
  };

  const getCurrentSortDirection = (columnKey: string): string | null => {
    const sortableParam = getSortableParamForColumn(columnKey);
    if (!sortableParam || !selectedSort) return null;

    const sortParam = sortableParam.query_param?.param1;
    const orderParam = sortableParam.query_param?.param2;

    if (selectedSort[sortParam] === columnKey) {
      return selectedSort[orderParam] || sortableParam.default_sort;
    }
    return null;
  };

  const handleSortClick = (columnKey: string) => {
    if (!sorting?.enabled || !onSortChange) return;

    const sortableParam = getSortableParamForColumn(columnKey);
    if (!sortableParam) return;

    const currentDirection = getCurrentSortDirection(columnKey);
    let newDirection;

    if (!currentDirection) {
      // No current sort, use default
      newDirection = sortableParam.default_sort;
    } else if (currentDirection === 'ASC') {
      newDirection = 'DESC';
    } else {
      newDirection = 'ASC';
    }

    const sortParam = sortableParam.query_param?.param1;
    const orderParam = sortableParam.query_param?.param2;

    onSortChange({
      [sortParam]: columnKey,
      [orderParam]: newDirection,
    });
  };

  const isFilterActive = (filter: Filter | undefined): boolean => {
    if (!filter || !selectedFilters) return false;

    const queryParam = filter.query_param || filter.queryParam;

    if (filter.type === 'date') {
      // Handle both string and object formats for date query params
      if (typeof queryParam === 'string') {
        const params = queryParam.split(',');
        return selectedFilters[params[0]] || selectedFilters[params[1]];
      } else if (typeof queryParam === 'object') {
        return (
          selectedFilters[queryParam.param1] ||
          selectedFilters[queryParam.param2]
        );
      }
    }

    const filterKey = getFilterKey(filter);
    return selectedFilters[filterKey] !== undefined;
  };

  const renderFilter = (filter: Filter | undefined): JSX.Element | null => {
    if (!filter) return null;

    // Parse filter options from fetch_options_api if available
    const getFilterOptions = (
      filter: Filter
    ): Array<{ value: any; label: string }> => {
      const filterKey = getFilterKey(filter);

      // Check for dynamic options first
      if (dynamicOptions[filterKey]) {
        return dynamicOptions[filterKey];
      }

      if (filter.options && Array.isArray(filter.options)) {
        return filter.options;
      }

      if (filter.fetch_options_api) {
        try {
          // Handle string representation of array
          let optionsArray;
          if (typeof filter.fetch_options_api === 'string') {
            // If it's a URL, we'll handle it in the useEffect above
            if (filter.fetch_options_api.startsWith('http')) {
              return []; // Will be populated by dynamic options
            }

            // Parse JSON string
            console.log(
              'Parsing JSON string for filter:',
              filterKey,
              'Raw string:',
              filter.fetch_options_api
            );
            try {
              optionsArray = JSON.parse(filter.fetch_options_api);
              console.log('Parsed options array:', optionsArray);
            } catch (parseError) {
              console.error('Failed to parse JSON string:', parseError);
              return [];
            }
          } else {
            optionsArray = filter.fetch_options_api;
          }

          // Check if options are already in the correct format
          if (
            optionsArray.length > 0 &&
            typeof optionsArray[0] === 'object' &&
            optionsArray[0].hasOwnProperty('value')
          ) {
            return optionsArray; // Already in correct format
          }

          // Convert to format expected by react-select
          return optionsArray.map(option => ({
            value: option,
            label: option,
          }));
        } catch (error) {
          console.error('Error parsing fetch_options_api:', error);
          return [];
        }
      }

      return [];
    };

    switch (filter.type) {
      case 'tags_modal':
        const tagsModalKey = getFilterKey(filter) || 'tags';
        return (
          <div className="column-filter">
            <TagsFilterInline
              placeholder={filter.placeholder || 'Tags Filter'}
              fetchOptionsApi={filter.fetch_options_api}
              initialValue={selectedFilters[tagsModalKey]}
              queryKey={tagsModalKey}
              onApply={(value: string | null) =>
                onFilterChange(tagsModalKey, value)
              }
            />
          </div>
        );

      case 'select':
        const filterOptions = getFilterOptions(filter);
        const selectFilterKey = getFilterKey(filter);

        // Hard-code: If this is the advanced tags filter, render inline tags filter
        if (
          filter.label === 'Tags (Advanced)' &&
          filter.fetch_options_api &&
          typeof filter.fetch_options_api === 'string' &&
          filter.fetch_options_api.includes('"tag_types"')
        ) {
          const tagsFilterKey = getFilterKey(filter) || 'tags';
          return (
            <div className="column-filter column-filter-left">
              <TagsFilterInline
                placeholder={filter.placeholder || 'Tags Filter'}
                fetchOptionsApi={filter.fetch_options_api}
                initialValue={selectedFilters[tagsFilterKey]}
                queryKey={tagsFilterKey}
                onApply={(value: string | null) =>
                  onFilterChange(tagsFilterKey, value)
                }
              />
            </div>
          );
        }

        // Hard-code: Assessments (Advanced) -> custom inline filter with assessment + value
        if (
          filter.label === 'Assessments (Advanced)' &&
          filter.fetch_options_api &&
          typeof filter.fetch_options_api === 'string' &&
          filter.fetch_options_api.includes('"assessments"')
        ) {
          const assessmentsKey = getFilterKey(filter) || 'assessments';
          return (
            <div className="column-filter column-filter-left">
              <AssessmentFilterInline
                placeholder={filter.placeholder || 'Assessment Filter'}
                fetchOptionsApi={filter.fetch_options_api}
                initialValue={selectedFilters[assessmentsKey]}
                queryKey={assessmentsKey}
                onApply={(value: string | null) =>
                  onFilterChange(assessmentsKey, value)
                }
              />
            </div>
          );
        }

        return (
          <div className="column-filter">
            <Select
              placeholder={filter.placeholder}
              onChange={selected =>
                onFilterChange(
                  selectFilterKey,
                  selected ? selected.value : null
                )
              }
              options={filterOptions || []}
              value={
                filterOptions?.find(
                  option => option.value === selectedFilters[selectFilterKey]
                ) || null
              }
              className="filter-select"
              isClearable
            />
          </div>
        );

      case 'multi_select':
        const multiSelectOptions = getFilterOptions(filter);
        const multiSelectQueryParam = getFilterKey(filter);
        console.log(
          'Multi-select filter options for',
          multiSelectQueryParam,
          ':',
          multiSelectOptions
        );
        return (
          <div className="column-filter">
            <Select
              placeholder={filter.placeholder}
              onChange={selected => {
                if (selected && selected.length > 0) {
                  // Format as [value1,value2] without quotes to match API expectations
                  const values = selected.map(option => option.value).join(',');
                  onFilterChange(multiSelectQueryParam, `[${values}]`);
                } else {
                  onFilterChange(multiSelectQueryParam, null);
                }
              }}
              options={multiSelectOptions || []}
              value={
                multiSelectOptions?.filter(option => {
                  const currentFilter = selectedFilters[multiSelectQueryParam];
                  if (
                    typeof currentFilter === 'string' &&
                    currentFilter.startsWith('[') &&
                    currentFilter.endsWith(']')
                  ) {
                    // Parse [value1,value2] format
                    const values = currentFilter.slice(1, -1).split(',');
                    return values.includes(option.value);
                  }
                  return (
                    Array.isArray(currentFilter) &&
                    currentFilter.includes(option.value)
                  );
                }) || []
              }
              isMulti={true}
              className="filter-select"
              closeMenuOnSelect={false}
              isClearable
            />
          </div>
        );

      case 'radio_button':
        const radioQueryParam = getFilterKey(filter);
        const radioOptions = getFilterOptions(filter);

        return (
          <div className="column-filter">
            {radioOptions.map((option, index) => (
              <div key={index} className="radio-group">
                <input
                  type="radio"
                  id={`${radioQueryParam}_${option.value}`}
                  name={radioQueryParam}
                  value={option.value}
                  checked={selectedFilters[radioQueryParam] === option.value}
                  onChange={e => {
                    const value = e.target.value;
                    onFilterChange(radioQueryParam, value);
                    if (filter.queryParamValue) {
                      onFilterChange(filter.queryParamValue, option.label);
                    }
                  }}
                />
                <label htmlFor={`${radioQueryParam}_${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'date':
        const dateQueryParam = filter.query_param || filter.queryParam;
        let startParam: string, endParam: string;

        console.log(
          'Date filter debug - dateQueryParam:',
          dateQueryParam,
          'type:',
          typeof dateQueryParam
        );

        // Handle both string and object formats for date query params
        if (typeof dateQueryParam === 'string') {
          const params = dateQueryParam.split(',');
          startParam = params[0]?.trim() || '';
          endParam = params[1]?.trim() || '';
          console.log('Date filter debug - split params:', {
            startParam,
            endParam,
          });
        } else if (typeof dateQueryParam === 'object') {
          startParam = dateQueryParam.param1;
          endParam = dateQueryParam.param2;
          console.log('Date filter debug - object params:', {
            startParam,
            endParam,
          });
        } else {
          startParam = '';
          endParam = '';
        }

        return (
          <div className="column-filter date-filter-dropdown">
            <DatePicker
              selectsRange={true}
              startDate={
                selectedFilters[startParam]
                  ? new Date(selectedFilters[startParam] * 1000)
                  : null
              }
              endDate={
                selectedFilters[endParam]
                  ? new Date(selectedFilters[endParam] * 1000)
                  : null
              }
              onChange={dates => {
                const [start, end] = dates;
                onFilterChange(
                  startParam,
                  start ? convertTimeStampToUnix(start) : null
                );
                onFilterChange(
                  endParam,
                  end ? convertTimeStampToUnix(end) : null
                );
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select date range..."
              isClearable
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
              monthsShown={2}
              inline
              calendarClassName="filter-calendar"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const toggleFilter = (columnName: string) => {
    // If opening a filter (not closing), scroll table to top
    if (openFilterColumn !== columnName) {
      // Find the table container and scroll to top to show filter
      const tableContainer = document.querySelector('.table-container');
      if (tableContainer && tableContainer.scrollTop > 0) {
        tableContainer.scrollTo({ top: 0, behavior: 'auto' });
      }
    }

    setOpenFilterColumn(openFilterColumn === columnName ? null : columnName);
  };

  return (
    <thead>
      <tr>
        {columns.map((column, index) => {
          const columnFilter = getFilterForColumn(column.label);
          const isActive = isFilterActive(columnFilter);
          const isOpen = openFilterColumn === column.label;

          // Check if this is an action column to apply matching padding
          const isActionColumn =
            column.type_ === 'icon' || column.type_ === 'complex_icon';
          const actionColumnPadding = isActionColumn
            ? { padding: '4px 8px 4px 20px' }
            : {};

          return (
            <th
              key={index}
              className={isOpen ? 'filter-open' : ''}
              style={
                columnWidths && columnWidths[column.key]
                  ? {
                      width: columnWidths[column.key],
                      minWidth:
                        columnMinWidths?.[column.key] ||
                        columnWidths[column.key],
                      boxSizing: 'border-box',
                      ...actionColumnPadding,
                    }
                  : column.width
                    ? {
                        width: column.width,
                        minWidth: columnMinWidths?.[column.key] || column.width,
                        boxSizing: 'border-box',
                        ...actionColumnPadding,
                      }
                    : {
                        boxSizing: 'border-box',
                        minWidth: columnMinWidths?.[column.key] || '150px',
                        ...actionColumnPadding,
                      }
              }
            >
              <div
                className={`th-content ${isOpen ? 'filter-open' : ''}`}
                style={{ width: '100%' }}
                ref={isOpen ? filterRef : null}
              >
                <div className="th-header">
                  {column.label}
                  {columnFilter && (
                    <button
                      className={`filter-toggle ${isActive ? 'active' : ''}`}
                      onClick={e => {
                        e.stopPropagation();
                        toggleFilter(column.label);
                      }}
                    >
                      {isActive ? <FilterIconActive /> : <FilterIcon />}
                    </button>
                  )}
                  {sorting?.enabled &&
                    getSortableParamForColumn(column.key) && (
                      <button
                        className="sort-toggle"
                        onClick={e => {
                          e.stopPropagation();
                          handleSortClick(column.key);
                        }}
                        title={`Sort by ${column.label}`}
                      >
                        {(() => {
                          const direction = getCurrentSortDirection(column.key);
                          if (direction === 'ASC') return '↑';
                          if (direction === 'DESC') return '↓';
                          return '↕';
                        })()}
                      </button>
                    )}
                </div>
                {isOpen && renderFilter(columnFilter)}
              </div>
              {/* Add resize handle */}
              {startColumnResize && index < columns.length - 1 && (
                <div
                  className="column-resize-handle"
                  onMouseDown={e => startColumnResize(e, column.key)}
                  title="Drag to resize column"
                >
                  <div className="resize-icon">⋮⋮</div>
                </div>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default TableHeader;
